import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import HeirloomView from '@/components/HeirloomView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PasscodeGate({ slug, lovedOneName, onVerified }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/heirloom/${slug}/verify`, { passcode: code });
      onVerified(data);
    } catch {
      setError('Invalid passcode. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center px-6" data-testid="passcode-gate">
      <div className="text-center max-w-sm">
        <div className="w-12 h-[1px] bg-[#A89F91] mx-auto mb-8" />
        <p className="overline mb-6">Private Archive</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] mb-3">
          {lovedOneName}
        </h1>
        <p className="font-['Cormorant_Garamond'] text-lg italic text-[#78716C] mb-10">Their story awaits.</p>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="passcode-input"
              className="w-full text-center text-xl tracking-[0.3em] bg-transparent border-b border-[#D6D3CD] focus:border-[#1C1917] py-3 outline-none transition-colors"
              placeholder="Enter passcode"
              autoFocus
            />
          </div>
          {error && <p className="text-red-600 text-sm" data-testid="passcode-error">{error}</p>}
          <button
            type="submit"
            disabled={loading || !code}
            data-testid="passcode-submit"
            className="w-full bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] disabled:opacity-50 py-3.5 text-sm font-medium tracking-wide transition-all duration-300"
          >
            {loading ? 'Verifying...' : 'Enter'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function HeirloomPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [needsPasscode, setNeedsPasscode] = useState(false);
  const [lovedOneName, setLovedOneName] = useState('');

  useEffect(() => {
    fetchHeirloom();
  }, [slug]);

  const fetchHeirloom = async () => {
    try {
      const { data } = await axios.get(`${API}/heirloom/${slug}`);
      if (data.needs_passcode) {
        setNeedsPasscode(true);
        setLovedOneName(data.loved_one_name);
      } else {
        setProject(data);
      }
    } catch {
      setError('This heirloom could not be found.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-8 h-8 border border-[#A89F91] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-sm text-[#A89F91] tracking-widest uppercase">Opening the archive</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center px-6">
        <div className="text-center" data-testid="heirloom-error">
          <h1 className="font-['Cormorant_Garamond'] text-4xl text-[#1C1917] mb-4">Not Found</h1>
          <p className="text-[#57534E]">{error}</p>
        </div>
      </div>
    );
  }

  if (needsPasscode && !project) {
    return <PasscodeGate slug={slug} lovedOneName={lovedOneName} onVerified={(data) => { setProject(data); setNeedsPasscode(false); }} />;
  }

  if (!project) return null;

  return <HeirloomView project={project} />;
}
